import { useQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from "@mui/material";
import { countyNamesMatch } from "encamp-shared/src/utils/county";
import { gql } from "generated-graphql";
import { useMemo } from "react";
import { FieldErrors } from "react-hook-form";

import { ErrorDisplay } from "./Forms/ErrorDisplay";

const T2M_AGENCIES_QUERY = gql(`
  query T2mAgencies($state: String!, $agencyType: String!) {
    t2mAgencies(state: $state, agencyType: $agencyType) {
      value
      display
      county
      municipality
    }
  }
`);

type T2mAgencyOption = {
  value: string;
  display: string;
  county: string;
  municipality?: string | null;
};

type T2mAgencyAutocompleteProps = {
  agencyType: "lepc" | "fd";
  state: string | null | undefined;
  county: string | null | undefined;
  portalId: string | null | undefined;
  portalName: string | null | undefined;
  onSelect: (portalId: string, portalName: string) => void;
  disabled?: boolean;
  errors?: FieldErrors<{ portalId?: string; portalName?: string }>;
};

const filterOptions = createFilterOptions<T2mAgencyOption>({
  matchFrom: "any",
  stringify: (option) =>
    `${option.value} ${option.display} ${option.county} ${option.municipality ?? ""}`,
});

/**
 * Autocomplete component for selecting T2M portal agencies (LEPCs or Fire Departments).
 * Shows rich options with county/municipality context.
 * Allows both selection from options and free text entry.
 */
export function T2mAgencyAutocomplete({
  agencyType,
  state,
  county,
  portalId,
  portalName,
  onSelect,
  disabled,
  errors,
}: T2mAgencyAutocompleteProps) {
  const { data, loading } = useQuery(T2M_AGENCIES_QUERY, {
    variables: { state: state ?? "", agencyType },
    skip: !state,
  });

  const options: T2mAgencyOption[] = useMemo(() => {
    const allAgencies = data?.t2mAgencies ?? [];
    // Filter by county if provided, except for CT which shows all agencies statewide.
    // Connecticut abolished county governments in 1960, so counties have no administrative
    // function. In 2022, the U.S. Census Bureau adopted Connecticut's nine Councils of
    // Governments (COGs) as county-equivalents. LEPCs and Fire Departments in CT operate
    // at the municipal or regional level rather than being organized by county, so we show
    // all agencies regardless of county.
    let filtered: T2mAgencyOption[];
    if (county && state !== "CT") {
      filtered = allAgencies.filter((agency) => countyNamesMatch(agency.county, county));
    } else {
      filtered = allAgencies;
    }

    // OR allows "None" as a valid LEPC option for facilities without an LEPC
    if (state === "OR" && agencyType === "lepc") {
      return [{ value: "-1", display: "None", county: "" }, ...filtered];
    }

    return filtered;
  }, [data?.t2mAgencies, county, state, agencyType]);

  // Find the currently selected option based on portalId AND portalName match.
  // We require both to match so that clicking the same portalId with a different
  // portalName will still trigger onChange (to correct the name).
  const currentValue = useMemo(() => {
    if (!portalId) return null;
    const matchingOption = options.find((opt) => opt.value === portalId);
    // Only return the option if the portalName also matches, otherwise
    // MUI won't fire onChange when re-selecting the same option
    if (matchingOption && matchingOption.display === portalName) {
      return matchingOption;
    }
    return null;
  }, [options, portalId, portalName]);

  // Always use portalName as the input value to allow free editing
  const inputValue = portalName ?? "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Autocomplete
        options={options}
        loading={loading}
        disabled={disabled}
        freeSolo
        filterOptions={filterOptions}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option.display;
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof value === "string") {
            return option.display === value || option.value === value;
          }
          return option.value === value.value;
        }}
        value={currentValue}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Handle free text input - update portalName but not portalId
          if (reason === "input") {
            onSelect(portalId ?? "", newInputValue);
          }
        }}
        onChange={(_, newValue) => {
          if (newValue && typeof newValue === "object" && "value" in newValue) {
            // Selected from dropdown - update both portalId and portalName
            onSelect(newValue.value, newValue.display);
          } else if (typeof newValue === "string") {
            // Free text entered and confirmed (e.g., pressing enter)
            onSelect("", newValue);
          } else {
            // Cleared
            onSelect("", "");
          }
        }}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <li key={option.value} {...otherProps}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body1">{option.display}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {option.value}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {option.municipality
                    ? `${option.county} · ${option.municipality}`
                    : option.county}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Portal Name"
            placeholder="Search by name or ID..."
            error={!!errors?.portalName}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <ErrorDisplay error={errors?.portalName} />
      <TextField
        label="Portal ID"
        value={portalId ?? ""}
        onChange={(e) => onSelect(e.target.value, portalName ?? "")}
        disabled={disabled}
        size="small"
        error={!!errors?.portalId}
      />
      <ErrorDisplay
        error={errors?.portalId}
        helperText="T2M Portal ID (auto-filled when selecting from dropdown)"
      />
    </Box>
  );
}
